import {
    createVerifiedRequestedScope,
    deriveVerifiedRfqContentProvenance,
    isVerifiedRfqContentProvenance,
    resolveVerifiedRfqScope,
} from './source-provenance';

type LooseForm = Record<string, unknown>;

/**
 * Drop the RFQ authority receipts. Used when the current documents can no
 * longer back them: a receipt that no document supports must not outlive its
 * source (removal, reclassification, incomplete extraction, replaced content).
 */
export function withoutStaleRfqAuthority<T extends LooseForm>(formData: T): T {
    if (!('rfqContentProvenance' in formData) && !('verifiedRequestedScope' in formData)) {
        return formData;
    }
    const {
        rfqContentProvenance: _rfqContentProvenance,
        verifiedRequestedScope: _verifiedRequestedScope,
        ...rest
    } = formData;
    return rest as T;
}

/**
 * Bind (or retire) the RFQ authority receipts for `formData` against the current
 * documents. The ONE place the receipts are minted; the Project Summary save and
 * the source-bound recovery both call it, so a recovered project gets exactly the
 * receipt a freshly imported one would.
 *
 * It only ever adds, replaces or removes `rfqContentProvenance` and
 * `verifiedRequestedScope`. Every other field, including the authored
 * `rfqContent`, is returned untouched.
 */
export function rebindVerifiedRfqAuthority<T extends LooseForm>(
    formData: T,
    documents: unknown[],
    preprocessedFiles: unknown[],
): T {
    const rfqContent = formData.rfqContent;
    const derived = deriveVerifiedRfqContentProvenance({
        rfqContent,
        documents,
        preprocessedFiles,
    });
    if (derived) {
        const verifiedRequestedScope = createVerifiedRequestedScope(rfqContent, derived);
        const next = {
            ...formData,
            rfqContentProvenance: derived,
        } as T;
        if (verifiedRequestedScope) {
            return { ...next, verifiedRequestedScope };
        }
        const { verifiedRequestedScope: _staleRequestedScope, ...withoutRequestedScope } = next;
        return withoutRequestedScope as T;
    }

    // A project reload may no longer carry the extracted document text in
    // memory. Preserve a previously persisted proof only while it still binds
    // to the exact RFQ content and its requested-scope rows still validate.
    if (isVerifiedRfqContentProvenance(formData.rfqContentProvenance, rfqContent)) {
        const resolved = resolveVerifiedRfqScope(formData as Record<string, unknown>);
        if (resolved || formData.verifiedRequestedScope == null) return formData;
        const { verifiedRequestedScope: _invalidRequestedScope, ...withoutRequestedScope } = formData;
        return withoutRequestedScope as T;
    }
    return withoutStaleRfqAuthority(formData);
}
